import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Container, Warehouse, ArrowUpFromLine, Activity, Maximize, Ship, LogOut } from "lucide-react";
import { api, MOVEMENT_LABELS, TRANSIT_LABELS, locationLabel } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

function BigStat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-4 flex flex-col justify-between" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="font-outfit font-extrabold text-4xl xl:text-5xl neon-pulse mt-2" style={{ color: accent }} data-testid={`${testid}-value`}>
        {value}
      </p>
    </div>
  );
}

export default function TVView() {
  const [clock, setClock] = useState(new Date());
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data } = useQuery({
    queryKey: ["tv-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 5000,
  });

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data: transitsRaw = [] } = useQuery({
    queryKey: ["tv-transits"],
    queryFn: async () => (await api.get("/transits")).data,
    refetchInterval: 5000,
  });

  const stats = data || { active_containers: 0, movements_today: 0, on_ramp: 0, off_ramp: 0, active_list: [] };
  const transits = transitsRaw
    .filter((t) => t.estado !== "llegado_panama")
    .sort((a, b) => (a.dias_restantes ?? 9999) - (b.dias_restantes ?? 9999));

  const goFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col p-4 xl:p-5 text-white select-none"
         style={{ background: "linear-gradient(180deg, #040814 0%, #0A1128 100%)" }} data-testid="tv-view">
      <header className="flex items-center justify-between">
        <Logo dark />
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="font-mono text-2xl xl:text-3xl font-bold text-telemetry tabular-nums" data-testid="tv-clock">
              {clock.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "America/Panama" })}
            </p>
            <p className="text-xs text-slate-400 uppercase tracking-widest">
              {clock.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/Panama" })}
            </p>
          </div>
          <button onClick={goFullscreen} className="p-2.5 rounded-lg border border-white/15 text-slate-300 hover:text-telemetry hover:border-telemetry/50 transition-colors"
                  data-testid="tv-fullscreen-button" title="Pantalla completa">
            <Maximize className="w-5 h-5" />
          </button>
          <button onClick={async () => { await logout(); navigate("/login"); }}
                  className="p-2.5 rounded-lg border border-white/15 text-slate-300 hover:text-red-400 hover:border-red-400/50 transition-colors"
                  data-testid="tv-logout-button" title="Cerrar sesión">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-4 gap-3 mt-4">
        <BigStat label="Contenedores Activos" value={stats.active_containers} icon={Container} accent="#00F0FF" testid="tv-active" />
        <BigStat label="En Rampa" value={stats.on_ramp} icon={Warehouse} accent="#00E676" testid="tv-on-ramp" />
        <BigStat label="Fuera de Rampa" value={stats.off_ramp} icon={ArrowUpFromLine} accent="#FFB703" testid="tv-off-ramp" />
        <BigStat label="Movimientos Hoy" value={stats.movements_today} icon={Activity} accent="#A855F7" testid="tv-today" />
      </div>

      <div className="flex-1 min-h-0 mt-4 grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden flex flex-col min-h-0" data-testid="tv-active-list">
          <div className="px-5 py-2.5 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-outfit font-bold text-base tracking-wide text-slate-200">Contenedores en Sitio</h2>
            <span className="flex items-center gap-2 text-[11px] text-emerald-400 font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> EN VIVO
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden px-5 py-2">
            {stats.active_list.length === 0 && (
              <p className="text-slate-500 text-center py-8" data-testid="tv-empty">Sin contenedores activos en este momento</p>
            )}
            <div className="grid grid-cols-2 gap-x-5">
              {stats.active_list.slice(0, 30).map((m) => (
                <div key={m.id} className="flex items-center justify-between py-1 border-b border-white/5" data-testid={`tv-container-${m.container_number}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 pulse-dot ${m.fuera_de_rampa ? "bg-amber-400" : "bg-emerald-400"}`} />
                    <span className="font-mono font-bold text-sm tracking-widest text-white">{m.container_number}</span>
                    <span className="text-[10px] text-slate-400 truncate">{m.size}</span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${m.fuera_de_rampa ? "text-amber-400" : "text-emerald-400"}`}>
                    {locationLabel(m)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden flex flex-col min-h-0" data-testid="tv-transit-list">
          <div className="px-5 py-2.5 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-outfit font-bold text-base tracking-wide text-slate-200 flex items-center gap-2">
              <Ship className="w-4 h-4 text-telemetry" /> En Tránsito a Panamá
            </h2>
            <span className="text-[11px] text-slate-400 font-mono" data-testid="tv-transit-count">{transits.length} activos</span>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden px-5 py-2">
            {transits.length === 0 && (
              <p className="text-slate-500 text-center py-8" data-testid="tv-transit-empty">Sin contenedores en tránsito</p>
            )}
            <div className="grid grid-cols-2 gap-x-5">
              {transits.slice(0, 30).map((t) => (
                <div key={t.id} className="flex items-center justify-between py-1 border-b border-white/5" data-testid={`tv-transit-${t.container_number}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 pulse-dot ${t.estado === "en_escala" ? "bg-amber-400" : "bg-sky-400"}`} />
                    <span className="font-mono font-bold text-sm tracking-widest text-white">{t.container_number}</span>
                    <span className="text-[10px] text-slate-400 truncate">{t.naviera || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400 hidden xl:inline">{TRANSIT_LABELS[t.estado]}</span>
                    <span className={`font-mono font-bold text-sm tabular-nums ${t.dias_restantes != null && t.dias_restantes <= 5 ? "text-red-400" : "text-telemetry"}`}
                          data-testid={`tv-transit-days-${t.container_number}`}>
                      {t.dias_restantes != null ? `${Math.max(0, t.dias_restantes)}d` : "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <footer className="mt-4 px-5 py-2 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-between text-[11px] text-slate-500 font-mono">
        <span>SOLOContainer · Sala de Operaciones</span>
        <span data-testid="tv-last-update">Actualización automática cada 5 s · Hora de Panamá</span>
      </footer>
    </div>
  );
}
